import React, { useState, useMemo, useCallback } from "react";
import {
  Button,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Typography,
  InputLabel,
  IconButton,
  Alert,
  Box,
  TextField,
  Select,
  Stack,
  MenuItem,
} from "@mui/material";

import {
  SendOutlined as SubmitIcon,
  CreditCard as CreditIcon,
  BadgeOutlined as BadgeIcon,
  SwapHoriz as SwapIcon,
} from "@mui/icons-material";
import { JobStatus } from "@taxassist/api-client";
import { useSubmitJobAsUser, useGetFirmAsUser } from "@taxassist/api-client";
import type { JobResponse } from "@taxassist/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { FilingStatus } from "../../types/JobTypes";
import {
  canSubmitJob,
  getSubmitTooltip,
  canSubmitJobWithCredits,
} from "../../utils/jobSubmissionUtils";
import { useSsnInputMask } from "../../hooks/useSsnInputMask";
import { useFirm } from "../../contexts/FirmContext";

interface SubmitJobButtonProps {
  job: JobResponse;
  onSuccess?: (job: JobResponse) => void;
  onError?: (error: Error, job: JobResponse) => void;
  variant?: "text" | "outlined" | "contained";
  size?: "small" | "medium" | "large";
  fullWidth?: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
}

export const SubmitJobButton = React.memo(
  ({
    job,
    onSuccess,
    onError,
    variant = "text",
    size = "medium",
    fullWidth = false,
    disabled = false,
    children,
  }: SubmitJobButtonProps) => {
    const queryClient = useQueryClient();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showCreditError, setShowCreditError] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [formData, setFormData] = useState({
      filingStatus: "",
      taxpayerSsn: "",
      spouseSsn: "",
    });

    // Validation state for fields
    const [validation, setValidation] = useState({
      filingStatus: { error: false, helperText: "" },
      taxpayerSsn: { error: false, helperText: "" },
      spouseSsn: { error: false, helperText: "" },
    });

    const isResubmission = useMemo(
      () => job.status === JobStatus.NEW_FILES || job.lastSubmittedAt,
      [job],
    );

    const isMarried = useMemo(
      () =>
        formData.filingStatus === FilingStatus.MarriedFilingJointly ||
        formData.filingStatus === FilingStatus.MarriedFilingSeparately,
      [formData.filingStatus],
    );

    const setTaxpayerSsn = useCallback((value: string) => {
      setFormData((prev) => ({ ...prev, taxpayerSsn: value }));
    }, []);

    const setSpouseSsn = useCallback((value: string) => {
      setFormData((prev) => ({ ...prev, spouseSsn: value }));
    }, []);

    const [taxpayerSsnRef, handleTaxpayerSsn] = useSsnInputMask(
      formData.taxpayerSsn,
      setTaxpayerSsn,
    );

    const [spouseSsnRef, handleSpouseSsn] = useSsnInputMask(
      formData.spouseSsn,
      setSpouseSsn,
    );

    const { currentFirmId } = useFirm();

    const refreshFirmCredits = useCallback(() => {
      if (!currentFirmId) {
        return;
      }

      queryClient.invalidateQueries({ queryKey: [`/firms/${currentFirmId}`] });
      queryClient.invalidateQueries({ queryKey: ["firm", currentFirmId] });
    }, [currentFirmId, queryClient]);

    // Get firm data to check credit availability
    const { data: firmData } = useGetFirmAsUser(currentFirmId || "disabled", {
      query: {
        queryKey: ["firm", currentFirmId],
        enabled: !!currentFirmId,
        refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
      },
    });

    // Submit job mutation
    const submitJobMutation = useSubmitJobAsUser({
      mutation: {
        onSuccess: () => {
          setIsSubmitting(false);
          refreshFirmCredits();
          onSuccess?.(job);
        },
        onError: (error) => {
          setIsSubmitting(false);

          // Check if error is credit-related
          const errorMessage = (error as any)?.message?.toLowerCase() || "";
          if (
            errorMessage.includes("credit") ||
            errorMessage.includes("insufficient")
          ) {
            setShowCreditError(true);
          } else {
            // Convert ErrorResponse to Error for the callback
            const errorObj = new Error(
              (error as any)?.message || "An error occurred",
            );
            onError?.(errorObj, job);
          }
        },
      },
    });

    const handleSubmitJob = useCallback(() => {
      if (!canSubmitJob(job) || isSubmitting || disabled) {
        return;
      }

      // Guard clause: Check if currentFirmId is available
      if (!currentFirmId) {
        const error = new Error(
          "No firm selected. Please select a firm before submitting jobs.",
        );
        onError?.(error, job);
        return;
      }

      // Check if firm has sufficient credits (only for DRAFT jobs, not for resubmissions)
      if (
        firmData &&
        firmData.totalCredits <= 0 &&
        job.status === JobStatus.DRAFT
      ) {
        setShowCreditError(true);
        return;
      }

      // Show confirmation dialog
      setShowConfirmation(true);
    }, [job, isSubmitting, disabled, firmData, currentFirmId, onError]);

    // Validation function for required fields
    const validateFields = useCallback(() => {
      let valid = true;
      const newValidation = {
        filingStatus: { error: false, helperText: "" },
        taxpayerSsn: { error: false, helperText: "" },
        spouseSsn: { error: false, helperText: "" },
      };

      if (!formData.filingStatus) {
        newValidation.filingStatus = {
          error: true,
          helperText: "Filing status is required.",
        };
        valid = false;
      }

      // SSN validation: must be 9 digits or XXX-XX-XXXX
      const ssnRegex = /^\d{3}-\d{2}-\d{4}$|^\d{9}$/;

      if (!formData.taxpayerSsn) {
        newValidation.taxpayerSsn = {
          error: true,
          helperText: "Taxpayer SSN is required.",
        };
        valid = false;
      } else if (!ssnRegex.test(formData.taxpayerSsn)) {
        newValidation.taxpayerSsn = {
          error: true,
          helperText: "Enter a valid SSN (XXX-XX-XXXX).",
        };
        valid = false;
      }

      if (isMarried) {
        if (!formData.spouseSsn) {
          newValidation.spouseSsn = {
            error: true,
            helperText: "Spouse SSN is required.",
          };
          valid = false;
        } else if (!ssnRegex.test(formData.spouseSsn)) {
          newValidation.spouseSsn = {
            error: true,
            helperText: "Enter a valid SSN (XXX-XX-XXXX).",
          };
          valid = false;
        }
      }

      setValidation(newValidation);
      return valid;
    }, [formData, isMarried]);

    const handleConfirmSubmit = useCallback(() => {
      if (!currentFirmId) {
        const error = new Error(
          "No firm selected. Please select a firm before submitting jobs.",
        );
        onError?.(error, job);
        return;
      }

      // Only validate for new submissions
      if (!isResubmission) {
        if (!validateFields()) {
          return; // Prevent submit if invalid
        }
      }

      setShowConfirmation(false);
      setIsSubmitting(true);

      submitJobMutation.mutate({
        firmId: currentFirmId,
        jobId: job.jobId,
        data: {
          metadata: {
            notes: `Submitted for ${job.clientName} - ${job.filingType} ${job.taxYear}`,
            filingStatus: formData.filingStatus,
            taxpayerSsn: formData.taxpayerSsn,
            spouseSsn: formData.spouseSsn,
          },
        },
      });
    }, [
      currentFirmId,
      onError,
      job,
      submitJobMutation,
      formData,
      isResubmission,
      validateFields,
    ]);

    const handleSwapClick = () => {
      setFormData((prev) => ({
        filingStatus: prev.filingStatus,
        taxpayerSsn: prev.spouseSsn,
        spouseSsn: prev.taxpayerSsn,
      }))
    }

    const handleCancelSubmit = useCallback(() => {
      setShowConfirmation(false);
    }, []);

    // Only show red warning if credits are needed and not available
    // For NEW_FILES or jobs with previous submissions, credits are not needed
    const hasNoCredits =
      firmData && firmData.totalCredits <= 0 && job.status === JobStatus.DRAFT;
    const noFirmSelected = !currentFirmId;
    const isDisabled =
      noFirmSelected ||
      !canSubmitJobWithCredits(job, firmData) ||
      isSubmitting ||
      disabled;

    const tooltipText = noFirmSelected
      ? "No firm selected"
      : getSubmitTooltip(job, isSubmitting, firmData);

    const handleCloseCreditError = useCallback(() => {
      setShowCreditError(false);
    }, []);

    return (
      <>
        <Tooltip title={tooltipText} arrow>
          <span>
            <Button
              variant={variant}
              size={size}
              fullWidth={fullWidth}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={size === "small" ? 16 : 20} />
                ) : (
                  <SubmitIcon />
                )
              }
              onClick={handleSubmitJob}
              disabled={isDisabled}
              sx={{
                fontWeight: 700,
                minWidth: size === "small" ? 90 : 110,
                ...(hasNoCredits && {
                  color: "error.main",
                  borderColor: "error.main",
                  "&:hover": {
                    borderColor: "error.dark",
                    backgroundColor: "error.light",
                  },
                }),
              }}
            >
              {children || (isSubmitting ? "Submitting" : "Submit")}
            </Button>
          </span>
        </Tooltip>

        {/* Credit Error Dialog */}
        <Dialog
          open={showCreditError}
          onClose={handleCloseCreditError}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CreditIcon color="error" />
            Insufficient Credits
          </DialogTitle>
          <DialogContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body1" gutterBottom>
                This firm has no available credits for job submission.
              </Typography>
            </Alert>

            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                To submit jobs, you need to:
              </Typography>
              <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                <Box component="li">
                  <Typography variant="body2">
                    Contact your administrator to add credits to this firm
                  </Typography>
                </Box>
                <Box component="li">
                  <Typography variant="body2">
                    Ensure any existing credits haven't expired
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{ mt: 2, p: 2, backgroundColor: "grey.50", borderRadius: 1 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Job Details:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {job.clientName} - {job.filingType} {job.taxYear}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Files ready: {job.uploadedFiles?.length || 0}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCreditError} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog */}
        <Dialog
          open={showConfirmation}
          onClose={handleCancelSubmit}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            Submit {job.uploadedFiles?.length || 0} files?
          </DialogTitle>
          <DialogContent>
            {isResubmission && (
              <Typography>New files will increase processing time.</Typography>
            )}
            {!isResubmission && (
              <>
                <Typography
                  color="text.secondary"
                  variant="body2"
                  sx={{
                    display: "inline-flex",
                    gap: 1,
                    alignItems: "center",
                    fontWeight: 500,
                    letterSpacing: "0.1px",
                  }}
                >
                  <BadgeIcon />
                  We need a little more info about{" "}
                  {job.clientName ?? "the client"} to submit.
                </Typography>
                <Stack sx={{ gap: 3, my: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel
                      id="filing-status-label"
                      sx={{ background: "#fff", px: 1 }}
                    >
                      Filing status
                    </InputLabel>
                    <Select
                      required
                      id="filing-status"
                      labelId="filing-status-label"
                      value={formData.filingStatus}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          filingStatus: e.target.value,
                        }))
                      }
                      error={validation.filingStatus.error}
                    >
                      {Object.values(FilingStatus).map((status: string) => (
                        <MenuItem key={status} value={status}>
                          {status}
                        </MenuItem>
                      ))}
                    </Select>
                    {validation.filingStatus.error && (
                      <Typography color="error" variant="caption">
                        {validation.filingStatus.helperText}
                      </Typography>
                    )}
                  </FormControl>
                  <Stack direction="row" sx={{ alignItems: 'center' }}>
                    <FormControl fullWidth>
                      <TextField
                        inputRef={taxpayerSsnRef}
                        required
                        id="taxpayer-ssn"
                        label="Taxpayer SSN"
                        type="tel"
                        value={formData.taxpayerSsn}
                        onChange={handleTaxpayerSsn}
                        inputMode="numeric"
                        inputProps={{
                          maxLength: 11,
                        }}
                        error={validation.taxpayerSsn.error}
                        helperText={validation.taxpayerSsn.helperText}
                      />
                    </FormControl>
                    {isMarried && (
                      <>
                        <IconButton onClick={handleSwapClick}>
                          <SwapIcon sx={{ color: "text.secondary" }} />
                        </IconButton>
                        <FormControl fullWidth>
                          <TextField
                            inputRef={spouseSsnRef}
                            required
                            id="spouse-ssn"
                            label="Spouse SSN"
                            type="tel"
                            value={formData.spouseSsn}
                            onChange={handleSpouseSsn}
                            inputMode="numeric"
                            inputProps={{
                              maxLength: 11,
                            }}
                            error={validation.spouseSsn.error}
                            helperText={validation.spouseSsn.helperText}
                          />
                        </FormControl>
                      </>
                    )}
                  </Stack>
                </Stack>
              </>
            )}
            <Alert severity="info" sx={{ mt: 2, fontWeight: "bold" }}>
              Do not open this client's file in your tax software after
              submitting.
            </Alert>
            {isResubmission && (
              <Alert severity="warning" sx={{ fontWeight: "bold", mt: 2 }}>
                This will override any changes you made for this client in your
                tax software.
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelSubmit} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSubmit}
              color="primary"
              variant="contained"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {isResubmission ? "Submit for 0 credits" : "Submit for 1 credit"}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  },
);

SubmitJobButton.displayName = "SubmitJobButton";
